import React from "react";
import { Scale, ShieldCheck, FileText, HeartHandshake } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pillars = [{
  title: "Legal Compliance",
  badge: "Integrity",
  description: "Our practice is built on lawful clarity.\nEvery method we use aligns with Indian regulations, ensuring your journey remains safe, secure, and dignified.",
  icon: Scale
}, {
  title: "Experienced Professionals",
  badge: "Expertise",
  description: "Experience defines the standard we uphold.\nOur team brings together deep expertise in law, psychological insight, and principled practice,\xA0 ensuring every engagement is handled with discernment, discretion, and unwavering professionalism.",
  icon: ShieldCheck
}, {
  title: "Clear Reporting",
  badge: "Clarity",
  description: "We present clear, verified information carefully put together and simple to understand.\nSo you can move ahead feeling informed, steady, and quietly confident in the choices before you.",
  icon: FileText
}, {
  title: "Dignified Conduct",
  badge: "Respect",
  description: "We understand the sensitivity that surrounds matrimonial decisions.\nOur approach remains measured and discreet, with respect for every individual involved ensuring clarity is pursued thoughtfully, and dignity is preserved at every step.",
  icon: HeartHandshake
}];
export default function OurEthicalFramework() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="our-ethical-framework" ref={ref} className="bg-background py-24 sm:py-32 relative overflow-hidden">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className={`max-w-2xl mx-auto text-center mb-20 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl font-serif font-medium text-foreground mb-6 tracking-tight">
            Our Ethical Framework
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed"><i></i><div data-ast-id="f:/src/pages/OurApproach/sections/OurEthicalFramework.tsx|sig:551ay4" data-ast-start="2302" data-ast-end="2483">That is why our foundation rests on four unwavering pillars created to safeguard your story, honor your privacy, and let certainty arrive gently, with quiet confidence.</div></p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {pillars.map((pillar, i) => <div key={pillar.title} data-index={i} className={`h-full transition-all duration-700 ease-out delay-[${i * 150}ms] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              <Card className="h-full border-border/40 bg-card/50 hover:bg-card hover:shadow-md transition-all duration-300 group">
                <CardHeader className="pb-4">
                  <div className="flex justify-between items-start mb-4">
                    <div className="p-2.5 rounded-full bg-secondary/50 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                      <pillar.icon className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <Badge variant="secondary" className="font-normal tracking-wide text-[10px] uppercase px-2.5 py-0.5 bg-secondary/30 text-secondary-foreground/80">
                      {pillar.badge}
                    </Badge>
                  </div>
                  <CardTitle className="font-serif text-xl text-foreground tracking-tight">
                    {pillar.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base leading-relaxed text-muted-foreground/90">
                    {pillar.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}